import _ from "lodash";
import React from "react";
import classNames from "classnames";
import { connect } from "react-redux";

import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  PencilOutlined,
  MinusCircleOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import GenericListView from "UIComponents/GenericListView";

import { getLearningGoalsDataMemoize } from "Assessments/modules/Utils";
import { getNodeParents } from "Utils/nodeParentHelpers";
import { generateNestedSelectedData, getStyleStrippedText } from "Utils";
import { generateFlatHierarchyWithValues } from "Utils/BenchmarkDataHelper";
import { htmlToText } from "Utils/HTMLHelpers";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { shouldShowFlatListForType } from "AppComponents/DatasetTaggingBody/modules/Utils";

import LearningGoalsModal from "../AddLearningGoals/components/LearningGoalsModal";
import DiscardLearningGoalsModal from "../AddLearningGoals/components/DiscardLearningGoalsModal";
import PopupForSubFields from "../AddLearningGoals/components/PopupForSubFields";
import classes from "./LearningGoalsNestedView.scss";
import ACLStore from "lib/aclStore";
import { getIsLearningGoalsRevampEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

import AssessmentLearningGoals from "AppComponents/AssessmentLearningGoals";
import { ASSESSMENT } from "Constants/stringConstants";

// start depth is 1 to keep styling consistent
// we will keep increasing depth of node by 1 as we go deeper
const START_DEPTH = 1;

const TreeNode = props => {
  const {
    value,
    label,
    subText,
    childrenNodes: nestedNodes,
    code,
    depth,
    showPopupForSubFields,
    togglePopover,
    updatePopoverRef,
    mode,
    getLearningGoalsTagCountTooltip,
    shouldUpdateActiveQuestionOnClick,
  } = props;
  const isLeaf = _.isEmpty(nestedNodes);
  const labelClasses = classNames({
    [classes.defaultLabel]: true,
    ["text-body-2"]: true,
    [classes.labelDepth2]: depth == 2,
    [classes.labelDepth3]: depth == 3,
    [classes.labelDepth4]: depth == 4,
    [classes.labelDepth5]: depth >= 5,
    [classes.listTypeLabel]: depth >= 2,
    [classes.rightEndLabelContainerClass]:
      isLeaf &&
      !_.isNil(
        getLearningGoalsTagCountTooltip?.(
          value,
          shouldUpdateActiveQuestionOnClick
        )
      ),
  });

  const listTypeClasses = classNames({
    [classes.listItemWithIcon]: showPopupForSubFields && isLeaf,
  });

  const codeClasses = classNames("text-textSubtle", classes.codeLabel);
  return (
    <>
      <div className={labelClasses}>
        <span className={listTypeClasses}>
          {htmlToText(label)}
          {code ? (
            <span
              className={codeClasses}
              dangerouslySetInnerHTML={{
                __html: ` (${getStyleStrippedText({
                  text: code,
                  type: "SNS_EDITOR",
                })})`,
              }}
            />
          ) : null}
          {ACLStore.can("FeatureFlag:EnableDescriptionInSNS") && subText && (
            <span style={{ marginLeft: "4px", verticalAlign: "middle" }}>
              <Tooltip
                tooltip={subText}
                overlayInnerStyle={{ maxWidth: "344px" }}
                showArrow={true}
              >
                <InformationOutlined
                  size="xxx-small"
                  variant="subtle"
                  style={{ position: "relative", top: "2px" }}
                />
              </Tooltip>
            </span>
          )}
          {showPopupForSubFields && isLeaf && mode === "edit" ? (
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              icon={<InformationOutlined variant="subtle" />}
              size="xx-small"
              onClick={() => togglePopover(value)}
              ref={node => updatePopoverRef(node, value)}
            />
          ) : null}
        </span>
        {isLeaf
          ? getLearningGoalsTagCountTooltip?.(
              value,
              shouldUpdateActiveQuestionOnClick
            )
          : null}
      </div>
      {!isLeaf
        ? _.map(nestedNodes, node => {
            const { id, label, children: childrenNodes, code } = node || {};

            return (
              <TreeNode
                key={id}
                value={id}
                label={label}
                childrenNodes={childrenNodes}
                code={code}
                depth={depth + 1}
                showPopupForSubFields={showPopupForSubFields}
                togglePopover={togglePopover}
                updatePopoverRef={updatePopoverRef}
                mode={mode}
                getLearningGoalsTagCountTooltip={
                  getLearningGoalsTagCountTooltip
                }
                subText={subText}
                shouldUpdateActiveQuestionOnClick={
                  shouldUpdateActiveQuestionOnClick
                }
              />
            );
          })
        : null}
    </>
  );
};

TreeNode.defaultProps = {
  getLearningGoalsTagCountTooltip: _.noop,
};

class LearningGoalsNestedView extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      isEditModalVisible: false,
      isRemoveModalVisible: false,
      popoverOptionId: null,
    };
    this.popoverRefs = [];
  }

  isValidValue = value => {
    return !!value && !_.isEmpty(value);
  };

  togglePopover = id => {
    const { popoverOptionId } = this.state;
    const updatedOptionId = popoverOptionId == id ? null : id;
    this.setState({ popoverOptionId: updatedOptionId });
  };

  closePopover = () => {
    this.setState({ popoverOptionId: null });
  };

  updatePopoverRef = (node, id) => {
    this.popoverRefs[id] = node;
  };

  onEditIconClick = e => {
    e?.stopPropagation();
    this.setState({
      isEditModalVisible: true,
    });
  };

  onRemoveIconClick = e => {
    e?.stopPropagation();
    this.setState({
      isRemoveModalVisible: true,
    });
  };

  onCloseLearningGoalsModal = () => {
    this.setState({ isEditModalVisible: false });
  };

  onCloseDiscardModal = () => {
    this.setState({ isRemoveModalVisible: false });
  };

  onRemoveLearningGoals = () => {
    const { updateInputField, fieldKey: plannerElement } = this.props;
    updateInputField({ [plannerElement]: [] }, { nodes: [], rootNodeId: "" });
  };

  getFilteredNodesBasedOnSubject = ({
    isSubjectPropInNode,
    nestedNodes,
    subjectId,
  }) => {
    if (isSubjectPropInNode) {
      return _.filter(nestedNodes, ({ subject } = {}) => {
        return subject == subjectId;
      });
    }
    return _.filter(nestedNodes, (node = {}) => {
      return _.find(getNodeParents(node, "SUBJECT"), { id: subjectId });
    });
  };

  getLearningGoalsDataParams = () => {
    const {
      unitPlanFields,
      assessmentFields,
      curriculumType,
      fieldKey,
      subFields,
      plannerElementConfig: { type, subType } = {},
      courseId,
      unitPlanFieldList,
      field_list,
      learningCourseId,
    } = this.props;

    const fieldObj = _.get(field_list, fieldKey, {});
    const config = _.get(fieldObj, "config", {});

    const { type: fieldType, options: fieldObjConfigOptions = [] } =
      config || {};

    const isPlannerElement = _.includes(
      ["PLANNER_ELEMENT", "PYP_PLANNER_ELEMENT"],
      fieldType
    );

    return {
      fieldKey,
      plannerElementType: type,
      unitPlanFields,
      assessmentFields,
      subFields,
      curriculumType,
      mode: "view",
      courseId,
      unitPlanFieldList,
      dataField: _.get(fieldObj, "dataField"),
      plannerElementSubType: subType,
      fieldObjConfigOptions,
      isPlannerElement,
      learningCourseId,
    };
  };

  renderPopover = () => {
    const { popoverOptionId } = this.state;
    const {
      subFields,
      unitPlanFieldList,
      unitPlanFields,
      fieldKey,
      field_list,
    } = this.props;
    const fieldObj = _.get(field_list, fieldKey, {});

    return (
      <PopupForSubFields
        optionId={popoverOptionId}
        subFields={subFields}
        closePopover={this.closePopover}
        childrenRef={this.popoverRefs[popoverOptionId]}
        unitPlanFieldList={unitPlanFieldList}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        dataField={_.get(fieldObj, "dataField")}
        emptyPopupSubText={_.get(fieldObj, "config.emptyPopupSubText")}
      />
    );
  };

  renderEditIcon = () => {
    const { t, testId } = this.props;

    return (
      <Tooltip tooltip={t("common:edit")} showArrow={true}>
        <div className={classes.editIcon}>
          <IconButton
            dsVersion="2.0"
            icon={<PencilOutlined variant="subtle" />}
            variant="neutral"
            type="plain"
            size="small"
            onClick={this.onEditIconClick}
            testId={`${testId}-edit`}
          />
        </div>
      </Tooltip>
    );
  };

  renderRemoveIcon = () => {
    const { t, testId, isFromQuestion } = this.props;
    const removeIconClasses = classNames(classes.removeIcon, {
      [classes.questionView]: isFromQuestion,
    });
    return (
      <Tooltip tooltip={t("common:remove")} showArrow={true}>
        <div className={removeIconClasses}>
          <IconButton
            dsVersion="2.0"
            icon={<MinusCircleOutlined variant="subtle" />}
            variant="neutral"
            type="plain"
            size="small"
            onClick={this.onRemoveIconClick}
            testId={`${testId}-remove`}
          />
        </div>
      </Tooltip>
    );
  };

  renderLearningGoalsModal = () => {
    const {
      unitPlanFields,
      assessmentFields,
      updateInputField,
      fieldKey,
      subFields,
      field_list,
      plannerElementConfig: {
        type: plannerElementType,
        subType: plannerElementSubType,
      } = {},
      selectedCourseId,
      assessmentType,
      unitPlanFieldList,
      templateId,
      assessmentTools,
      testId,
      label,
      getLearningGoalsTagCountTooltip,
      curriculumProgram,
      learningCourseId,
    } = this.props;

    const fieldObj = _.get(field_list, fieldKey, {});

    const { config = {}, type, dataField } = fieldObj;

    const {
      type: fieldType,
      options: fieldObjConfigOptions = [],
      showSearchFilter,
      showSideNavigation,
      showRadioButton,
      showPopupForSubFields,
      emptyPopupSubText,
    } = config || {};

    const isPlannerElement = _.includes(
      ["PLANNER_ELEMENT", "PYP_PLANNER_ELEMENT"],
      fieldType
    );

    return (
      <LearningGoalsModal
        type={type}
        fieldKey={fieldKey}
        fieldLabel={label}
        unitPlanFields={unitPlanFields}
        assessmentFields={assessmentFields}
        plannerElementType={plannerElementType}
        updateInputField={updateInputField}
        onCloseLearningGoalsModal={this.onCloseLearningGoalsModal}
        subFields={subFields}
        isPlannerElement={isPlannerElement}
        selectedCourseId={selectedCourseId}
        assessmentType={assessmentType}
        unitPlanFieldList={unitPlanFieldList}
        dataField={dataField}
        emptyPopupSubText={emptyPopupSubText}
        templateId={templateId}
        plannerElementSubType={plannerElementSubType}
        assessmentTools={assessmentTools}
        testId={testId}
        fieldObjConfigOptions={fieldObjConfigOptions}
        showSearchFilter={showSearchFilter}
        showSideNavigation={showSideNavigation}
        showRadioButton={showRadioButton}
        showPopupForSubFields={showPopupForSubFields}
        getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
        shouldUpdateActiveQuestionOnClick={false}
        curriculumProgram={curriculumProgram}
        learningCourseId={learningCourseId}
      />
    );
  };

  renderAssessmentLearningGoals = () => {
    const {
      fieldKey,
      field_list,
      unitPlanFields,
      courseSubjects,
      updateInputField,
      assessmentFields,
      hasAddRemoveValues,
      assignmentSubjects,
      selectedLearningGoal,
      defaultOptions,
      unitPlanTemplate,
      getLearningGoalsTagCountTooltip,
      subFields,
      parentId,
      parentType,
    } = this.props;

    return (
      <AssessmentLearningGoals
        field_list={field_list}
        unitPlanFields={unitPlanFields}
        courseSubjects={courseSubjects}
        updateInputField={updateInputField}
        assessmentFields={assessmentFields}
        hasAddRemoveValues={hasAddRemoveValues}
        assignmentSubjects={assignmentSubjects}
        selectedLearningGoal={selectedLearningGoal}
        defaultOptions={defaultOptions}
        unitPlanTemplate={unitPlanTemplate}
        getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
        subFields={subFields}
        onClose={this.onCloseLearningGoalsModal}
        selectedLearningGoalKey={fieldKey}
        assessmentId={parentType === ASSESSMENT ? parentId : null}
      />
    );
  };

  renderLearningGoalsRemoveModal = () => {
    const { t, assessmentType, label, isFromQuestion, testId } = this.props;

    const { value } = getLearningGoalsDataMemoize(
      this.getLearningGoalsDataParams()
    );

    const totalSelectedNodes = _.isArray(value)
      ? _.size(value)
      : _.size(_.compact(_.flatten(_.values(value))));

    const assessmentTypeTitle =
      assessmentType == "le"
        ? t("common:learning_experience")
        : isFromQuestion
        ? t("common:question")
        : t("common:assessment");

    const message = t("classRoom:remove_selected_learning_goals_message", {
      count: totalSelectedNodes,
      label: label,
      type: assessmentTypeTitle,
    });

    return (
      <DiscardLearningGoalsModal
        headerTitle={t("classRoom:remove_selected_learning_goals")}
        message={message}
        buttonLabel1={t("common:keep")}
        buttonLabel2={t("common:remove")}
        onClickButton1={this.onCloseDiscardModal}
        onClickButton2={this.onRemoveLearningGoals}
        onCloseDiscardModal={this.onCloseDiscardModal}
        testId={testId}
      />
    );
  };

  renderTreeView = ({ nodes, showPopupForSubFields }) => {
    const { mode, getLearningGoalsTagCountTooltip } = this.props;
    return _.map(nodes, node => {
      const { id, label, children: childrenNodes, code, subText } = node || {};
      return (
        <TreeNode
          key={id}
          value={id}
          label={label}
          childrenNodes={childrenNodes}
          code={code}
          depth={START_DEPTH}
          showPopupForSubFields={showPopupForSubFields}
          togglePopover={this.togglePopover}
          updatePopoverRef={this.updatePopoverRef}
          mode={mode}
          getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
          shouldUpdateActiveQuestionOnClick={true}
          subText={subText}
        />
      );
    });
  };

  renderFlatListView = ({ nodes, isStandards }) => {
    const { value } = this.props;
    const { flatNodes, rootNodeIds } = generateFlatHierarchyWithValues({
      nodes,
      values: value,
      includeNodeSet: isStandards,
    });

    return <GenericListView nodes={flatNodes} rootNodeIds={rootNodeIds} />;
  };

  renderNestedList = () => {
    const {
      fieldKey,
      field_list,
      plannerElementConfig: { type: plannerElementType } = {},
    } = this.props;

    const params = this.getLearningGoalsDataParams();

    const {
      value,
      nodes,
      rootNodes,
      parentNodes,
      groupByType,
      isSubjectPropInNode = false,
    } = getLearningGoalsDataMemoize(params);

    const fieldObj = _.get(field_list, fieldKey, {});
    const showPopupForSubFields = _.get(
      fieldObj,
      "config.showPopupForSubFields",
      false
    );

    const { isPlannerElement } = params;
    if (!isPlannerElement) {
      return this.renderTreeView({
        nodes,
        showPopupForSubFields,
      });
    }

    const { showFlatList, isStandards } = shouldShowFlatListForType({
      plannerElementType,
    });
    if (showFlatList) {
      return this.renderFlatListView({
        nodes,
        isStandards,
      });
    }

    const nestedNodes = generateNestedSelectedData({
      nodes,
      rootNodes,
      value,
    });

    const isStandardSetPresent =
      !_.isEmpty(_.get(_.first(nodes), "standardsWrapper.standardSet", {})) &&
      ACLStore.can("AdminPortal:StandardsV2");

    // Case to handle course standards in toddle 2.0
    // Instead of grouping by subject, we group by standard set
    if (isStandardSetPresent) {
      const uniqueStandardSets = _.uniqBy(
        _.map(nodes, node => {
          return _.get(node, "standardsWrapper.standardSet", {});
        }),
        "id"
      );

      return _.map(uniqueStandardSets, standardSet => {
        const filteredNodes = _.filter(nestedNodes, node => {
          return (
            _.get(node, "standardsWrapper.standardSet.id") === standardSet?.id
          );
        });

        if (!_.isEmpty(filteredNodes)) {
          return (
            <div className={classes.subjectContainer} key={standardSet?.id}>
              <div className="text-label-3">{standardSet?.title}</div>
              <div className={classes.nodesContainer}>
                {this.renderTreeView({
                  nodes: filteredNodes,
                  showPopupForSubFields,
                })}
              </div>
            </div>
          );
        }

        return null;
      });
    }

    if (groupByType === "SUBJECT" && !_.isEmpty(parentNodes)) {
      return _.map(parentNodes, ({ id, name }) => {
        const filteredNodes = this.getFilteredNodesBasedOnSubject({
          nestedNodes,
          subjectId: id,
          isSubjectPropInNode,
        });

        if (!_.isEmpty(filteredNodes)) {
          return (
            <div className={classes.subjectContainer} key={id}>
              <div className="text-label-3">{name}</div>
              <div className={classes.nodesContainer}>
                {this.renderTreeView({
                  nodes: filteredNodes,
                  showPopupForSubFields,
                })}
              </div>
            </div>
          );
        }

        return null;
      });
    }

    return this.renderTreeView({
      nodes: nestedNodes,
      showPopupForSubFields,
    });
  };

  renderView = () => {
    return (
      <div className={classes.container}>
        <div className={classes.innerContainer}>{this.renderNestedList()}</div>
      </div>
    );
  };

  renderEdit = () => {
    const {
      isEditModalVisible,
      isRemoveModalVisible,
      popoverOptionId,
    } = this.state;

    const { fieldKey, field_list } = this.props;
    const isLearningGoalsRevampEnabled = getIsLearningGoalsRevampEnabled();

    const fieldObj = _.get(field_list, fieldKey, {});
    const showPopupForSubFields = _.get(
      fieldObj,
      "config.showPopupForSubFields",
      false
    );
    const showPopover = showPopupForSubFields && !_.isEmpty(popoverOptionId);

    const renderEditModal = () => {
      if (!isEditModalVisible) {
        return null;
      }
      return isLearningGoalsRevampEnabled
        ? this.renderAssessmentLearningGoals()
        : this.renderLearningGoalsModal();
    };

    return (
      <div className={classes.container}>
        <div className={classes.innerContainer}>{this.renderNestedList()}</div>
        {renderEditModal()}
        {isRemoveModalVisible ? this.renderLearningGoalsRemoveModal() : null}
        {this.renderEditIcon()}
        {this.renderRemoveIcon()}
        {showPopover ? this.renderPopover() : null}
      </div>
    );
  };
}

const mapStatetoProps = (state, ownProps) => {
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );
  const courseId = ownProps?.selectedCourseId || selectedCourseId;

  return {
    courseId,
    curriculumType:
      getCurrentCurriculumProgramType(state) ||
      _.get(ownProps, "curriculumType"),
  };
};

export default connect(mapStatetoProps, null)(LearningGoalsNestedView);
